import { posthog } from 'posthog-js'
import Vue from 'vue'
import { isAccessLogsUrl } from '~/helpers/access-logs'
import { isTransportFailure } from '~/helpers/network-errors'

/**
 * Android in-app browsers (a host app's WebView, not Chrome) inject their own JS
 * bridge into every page and hook it up to `beforeunload`/`pagehide`
 * (`sendBeforeUnloadMessage` -> `sendDataToNative` -> `bridge.postMessage`). When
 * the app tears the WebView down while the page is still unloading, the Java side
 * of the bridge is already released and the WebView throws one of these two
 * messages from inside the injected handler.
 *
 * None of that code is ours — the frames are "Unknown Source" because the script
 * never existed on our origin — so there is nothing to fix or act on. Filter by
 * the message, which is fixed WebView wording, since the host app's function
 * names differ between apps.
 */
const ANDROID_WEBVIEW_BRIDGE_ERROR = /Java object is gone|Java exception was raised during method invocation/i

function isAndroidWebViewBridgeError (value) {
  const message = typeof value === 'string' ? value : value?.message
  return typeof message === 'string' && ANDROID_WEBVIEW_BRIDGE_ERROR.test(message)
}

function shouldIgnoreError (value) {
  return Boolean(
    value?.cm24SuppressReporting ||
    value?.config?.meta?.suppressGlobalError ||
    isAccessLogsUrl(value?.config?.url) ||
    isAccessLogsUrl(value?.message) ||
    // window.onerror hands us a bare message string when the browser withheld the
    // error object, so the value itself may be what needs matching.
    isAccessLogsUrl(value) ||
    isAndroidWebViewBridgeError(value)
  )
}

/**
 * Errors thrown by a cross-origin script that was loaded without CORS headers are
 * censored by the browser: no error object, the literal message "Script error.",
 * and zeroed filename/lineno/colno. That covers our third-party tags (userreport,
 * toast CDN, Google Maps, GTM-injected tags) and, more often, scripts injected by
 * browser extensions.
 *
 * There is nothing to act on in one of these — no stack, no source, no location —
 * so wrapping the message in `new Error(...)` only produced a large, permanently
 * unactionable issue whose only stack frames pointed back at this file.
 */
function isOpaqueCrossOriginError (ev) {
  if (ev.error) {
    return false
  }

  return /^script error\.?$/i.test(String(ev.message || '')) ||
    (!ev.filename && !ev.lineno)
}

function asError (value) {
  if (value instanceof Error) {
    return value
  }

  // DOMException in Safari is sometimes not an Error instance
  if (value && typeof value === 'object') {
    const name = value.name || 'NonError'
    const message = value.message || JSON.stringify(value)
    const err = new Error(`${name}: ${message}`)
    err.name = name
    err.cause = value
    return err
  }

  return new Error(String(value))
}

/**
 * Fraction of sessions to record for session replay, 0–1. Set here rather than in
 * the PostHog project settings because a client value takes precedence over the
 * remote config, so the rate is versioned with the code that ships it.
 *
 * The decision is made once per session: a sampled-out session records nothing,
 * so replay coverage is per-session, not per-pageview.
 */
const SESSION_RECORDING_SAMPLE_RATE = 0.1

export default function (ctx, inject) {
  const isStagingHost = process.client && window.location?.hostname?.includes('staging')
  const shouldDisablePosthog = Boolean(ctx.$config?.posthogDisabled) || isStagingHost

  if (shouldDisablePosthog) {
    const noop = () => {
    }
    const stub = {
      capture: noop,
      captureException: noop,
      identify: noop,
      register: noop,
      opt_out_capturing: noop,
      reset: noop
    }
    inject('posthog', stub)
    return
  }

  const {
    app,
    store
  } = ctx
  const router = app && app.router

  const customer = store.state?.customer?.customer
  const customerId = customer?.customerId
  const email = customer?.email
  const name = customer?.name
  const surname = customer?.surname

  const cm24Uuid = process.client ? ctx.$cookies.get('cm24-uuid') : undefined
  const sessionId = process.client ? ctx.$cookies.get('X-Session-Id') : undefined

  let posthogInitialized = false

  // Initialize PostHog only after consent is granted
  const initPostHog = () => {
    if (posthogInitialized) {
      return
    }

    console.log('initPostHog')
    posthog.init('phc_El08xDNvzjlFY0A8X497JERg8wPnxK1IfWM4MI8o0Td', {
      // api_host: 'https://eu.i.posthog.com',
      api_host: 'https://x.caravanmarkt24.de', // your managed reverse proxy domain
      ui_host: 'https://eu.posthog.com',
      defaults: '2025-05-24',
      // debug: true,
      capture_pageview: !process.dev,
      opt_out_capturing_by_default: process.dev,
      enable_sourcemaps: true,
      capture_exceptions: false,
      session_recording: {
        sampleRate: SESSION_RECORDING_SAMPLE_RATE
      },
      before_send: (event) => {
        if (customerId && event.properties) {
          event.properties.customerId = String(customerId)
        }
        if (email && event.properties) {
          event.properties.email = email
        }
        return event
      },
      loaded: () => {
        if (customerId) {
          posthog.identify(String(customerId), {
            email,
            name,
            surname,
            'cm24-uuid': cm24Uuid,
            'x-session-id': sessionId
          })
        } else {
          posthog.register({
            'cm24-uuid': cm24Uuid,
            'x-session-id': sessionId
          })
        }
      }
    })

    posthogInitialized = true
  }

  // Wait for CookieHub consent before initializing PostHog
  if (process.client) {
    // Check if consent is already granted
    const checkConsent = () => {
      if (window.cookiehub && window.cookiehub.hasConsented('analytics')) {
        initPostHog()
      }
    }

    document.addEventListener('cookiehub_onInitialise', (eventData) => {
      const data = eventData.detail
      if (data.category === 'analytics') {
        // console.log('CookieHub - onInitialise ... init posthog')
        initPostHog()
      }
    })
    document.addEventListener('cookiehub_onAllow', (eventData) => {
      const data = eventData.detail
      if (data.category === 'analytics') {
        // console.log('CookieHub - onAllow ... init posthog')
        initPostHog()
      }
    })

    // Check consent immediately if CookieHub is already loaded
    if (window.cookiehub) {
      checkConsent()
    } else {
      // Wait for CookieHub to load
      window.addEventListener('load', checkConsent)
    }
  }

  // Ensure opt-out at runtime in development
  try {
    if (process.dev && posthogInitialized) {
      posthog.opt_out_capturing()
    }
  } catch (e) {
    console.warn('Error while setting PostHog opt-out:', e)
  }

  // A helper to enrich every exception with consistent context
  const baseExceptionProps = () => ({
    route: router?.currentRoute?.fullPath,
    cm24Uuid,
    sessionId,
    customerId,
    email,
    name,
    surname
  })

  /**
   * A request that never reached the API carries no status, no body and no stack
   * worth reading — the browser collapses "aborted by a route change", "device
   * offline", "blocked by an extension" and "connection reset" into one opaque
   * "Network Error". Reported as exceptions they form a single huge, permanently
   * unactionable issue that buries real API failures.
   *
   * Keep the volume signal as a plain event instead: a spike on one endpoint
   * still means something (a bad deploy, a CORS regression), a steady trickle is
   * just the mobile web.
   */
  const report = (value, props) => {
    if (!posthogInitialized) {
      return
    }

    if (isTransportFailure(value)) {
      posthog.capture('[error] api request did not reach the server', {
        ...props,
        request_url: value.url,
        request_method: value.method
      })
      return
    }

    posthog.captureException(asError(value), props)
  }

  // Inject PostHog into the application and make it available via this.$posthog (or app.$posthog)
  inject('posthog', posthog)

  // Make sure that pageviews are captured with each route change
  router.afterEach((to, from) => {
    // Use Vue.nextTick to make sure the route is changed before capturing the pageview
    // eslint-disable-next-line import/no-named-as-default-member
    Vue.nextTick(() => {
      /* Note: this might also be a good place to call posthog.register(...) in order to update your properties
         on each page view
      */
      if (posthogInitialized) {
        posthog.capture('$pageview', {
          $current_url: to.fullPath
        })
      }
    })
  })

  // Vue (component) errors -> PostHog

  const prevVueHandler = Vue.config.errorHandler
  Vue.config.errorHandler = function (err, vm, info) {
    if (shouldIgnoreError(err)) {
      return
    }

    const componentName =
      vm?.$options?.name ||
      vm?.$options?._componentTag ||
      vm?.$vnode?.componentOptions?.Ctor?.options?.name

    report(err, {
      ...baseExceptionProps(),
      mechanism: 'vue',
      vue_info: info,
      vue_component: componentName
    })

    if (prevVueHandler) {
      prevVueHandler(err, vm, info)
    }
  }

  if (process.client) {
    window.addEventListener('error', (ev) => {
      // Some browsers provide ev.error, Safari sometimes not — check whatever we
      // would actually report, not just the object form.
      const value = ev.error || ev.message || 'window.error'

      if (shouldIgnoreError(value)) {
        return
      }

      if (isOpaqueCrossOriginError(ev)) {
        // Keep the volume signal — a third-party tag that starts throwing on every
        // page load is still worth noticing — but not as an exception.
        if (posthogInitialized) {
          posthog.capture('[error] opaque cross-origin script error', {
            ...baseExceptionProps(),
            filename: ev.filename || undefined
          })
        }

        return
      }

      report(value, {
        ...baseExceptionProps(),
        mechanism: 'window.error',
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno
      })
    })

    window.addEventListener('unhandledrejection', (ev) => {
      if (shouldIgnoreError(ev.reason)) {
        return
      }

      report(ev.reason, {
        ...baseExceptionProps(),
        mechanism: 'unhandledrejection'
      })
    })
  }
}
